///<reference path="../../../../typings/signalR.d.ts"/>
import * as angular from "angular";
import * as _ from "lodash";
import {States} from "../../shared/config/constant.module";
import AuthDataServiceName, {AuthDataService} from "../auth/authData.service";
import stringToDate from "../utility/stringToDate";

let requires = [AuthDataServiceName];

export interface SignalRHubOptionsBase {
  rootPath?: string;
  logging?: boolean;
  transport?: signalR.HttpTransportType;
  listeners?: any;
  allowMultipleListeners?: boolean;
  methods: string[];
  postConnect?: (hub: SignalRHub) => void;
  onStateChange?: (change: SignalRStateChange) => void;
}

export interface SignalRHubOptions extends SignalRHubOptionsBase {
  name: string;
}

export interface SignalRStateChange {
  newState: signalR.HubConnectionState;
  oldState: signalR.HubConnectionState;
}

export interface SignalRHub {
  connection: signalR.HubConnection;
  invoke(method: string, ...args: any[]): Promise<any>;
  disconnect(): Promise<any>;
  connect(): Promise<any>;
  promise: Promise<any> | null;
  uninitialize(): void;
  registerListeners(): void;
  deregisterListeners(): void;
  invokeWithRetry(method: () => Promise<any>): Promise<any>;
}

export interface SignalRHubCtor {
  new(options: SignalRHubOptions): SignalRHub;
}

class SignalRHubImpl implements SignalRHub {
  connection: signalR.HubConnection;
  promise: Promise<any> | null;
  static $: JQueryStatic;
  static $state: angular.ui.IStateService;
  static $window: angular.IWindowService;
  static $log: angular.ILogService;
  static authDataService: AuthDataService;
  private options: SignalRHubOptions;

  private static initNewConnection(options: SignalRHubOptions): signalR.HubConnection {
    var useLogging = (options && options.logging ? true : false);
    var connectionBuilder = new signalR.HubConnectionBuilder()
      .withUrl(options.rootPath + '/' + options.name, {
        accessTokenFactory: () => SignalRHubImpl.authDataService.getToken(),
        transport: options.transport || signalR.HttpTransportType.WebSockets
      });
    
    if (useLogging) {
      connectionBuilder = connectionBuilder.configureLogging({
        log: (logLevel: signalR.LogLevel, message: string) => {
          if (message === 'Failed to start the HttpConnection before stop() was called.') {
            //ignore
            return;
          }
          
          switch (logLevel) {
            case signalR.LogLevel.Information:
              SignalRHubImpl.$log.info(message);
              break;
            case signalR.LogLevel.Error:
            case signalR.LogLevel.Critical:
              SignalRHubImpl.$log.error(message);
              break;
            case signalR.LogLevel.Warning:
              SignalRHubImpl.$log.warn(message);
              break;
          }
        }
      });
    }

    return connectionBuilder.withAutomaticReconnect().build();
  }

  invoke(method: string, ...args: any[]): Promise<any> {
    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      return $.when(null) as Promise<any>;
    }

    return (<Promise<any>>this.connection.invoke.apply(this.connection, arguments))
      .catch(error => {
        if (error && error.context && error.context.status === 401) {
          this.connection.stop();
          SignalRHubImpl.$state.go(States.SIGN_IN);
        } else {
          throw error;
        }
      });
  }

  invokeWithRetry(method: () => Promise<any>): Promise<any> {
    return method().catch(() => {
      if (this.connection.state !== signalR.HubConnectionState.Connected) {
        return $.when(null) as Promise<any>;
      }

      return this.invokeWithRetry(method);
    });
  }

  connect(): Promise<any> {
    return this.connection.start().then(() => {
      this.runOnStateChange({
        newState: signalR.HubConnectionState.Connected,
        oldState: signalR.HubConnectionState.Disconnected
      });
    }, (e: Error) => {
      // ignore error if disconnecting before connection completed
      if (e.message !== 'Failed to start the HttpConnection before stop() was called.') {
        throw e;
      }
    });
  }

  disconnect(): Promise<any> {
    this.promise = null;
    return this.connection.stop().catch((e: Error) => {      
      // ignore error if disconnecting before connection completed
      if (e.message !== 'The connection was stopped before the hub handshake could complete.') {
        throw e;
      }
    });
  }

  deregisterListeners(): void {
    if (this.options && this.options.listeners) {
      for (const event in this.options.listeners) {
        this.connection.off(event, () => {});
      }
    }
  }

  private runPostConnect(): void {
    if (this.options && this.options.postConnect) {
      this.options.postConnect(this);
    }
  }

  private runOnStateChange(change: SignalRStateChange): void {
    if (this.options && this.options.onStateChange) {
      this.options.onStateChange(change);
    }
    if (change.newState === signalR.HubConnectionState.Connected
        && change.oldState !== signalR.HubConnectionState.Connected) {
      this.runPostConnect();  
    }
  }

  registerListeners(): void {
    if (!this.options.listeners) {
      return;
    }

    for (const event in this.options.listeners) {
      if (!this.options.allowMultipleListeners) {
        this.connection.off(event, () => {});
      }

      var hub = this;
      const fn = this.options.listeners[event];
      this.connection.on(event, function() {          
        stringToDate(arguments);
        return fn.apply(hub, arguments);
      });
    }
  }

  private registerMethods(): void {
    if (!this.options.methods) {
      return;
    }

    var hub = this;
    for (const method of this.options.methods) {
      hub[method] = function() {
        var args = $.makeArray(arguments);
        args.unshift(method);
        return hub.invoke.apply(hub, args);
      };
    }
  }

  private initialize(): void {
    this.registerListeners();
    this.registerMethods();
    this.connection.onclose(() => {
      this.runOnStateChange({
        newState: signalR.HubConnectionState.Disconnected,
        oldState: signalR.HubConnectionState.Connected
      });
    });
    this.connection.onreconnecting(() => {
      this.runOnStateChange({
        newState: signalR.HubConnectionState.Reconnecting,
        oldState: signalR.HubConnectionState.Disconnected
      });
    });
    this.connection.onreconnected(() => {
      this.runOnStateChange({
        newState: signalR.HubConnectionState.Connected,
        oldState: signalR.HubConnectionState.Reconnecting
      });
    });
    this.promise = this.connect();
  }

  uninitialize() {
    this.deregisterListeners();
    this.disconnect();
  }

  constructor(options: SignalRHubOptions) {
    this.connection = SignalRHubImpl.initNewConnection(options);
    this.options = options;
    this.initialize();
  }
}

export default angular.module('SignalR', requires)
  .constant('$', jQuery)
  .factory('Hub', ($: JQueryStatic,
                   $state: angular.ui.IStateService,
                   $window: angular.IWindowService,
                   $log: angular.ILogService,
                   authDataService: AuthDataService): SignalRHubCtor => {
    SignalRHubImpl.$ = $;
    SignalRHubImpl.$state = $state;
    SignalRHubImpl.$window = $window;
    SignalRHubImpl.$log = $log;
    SignalRHubImpl.authDataService = authDataService;
    return SignalRHubImpl;
  })
  .name;



// WEBPACK FOOTER //
// ./src/app/services/hubs/signalr-hub.ts